import { Box } from '@mui/material';
import { api, apiSchemas } from '@verifime/api-definition';
import {
  DisplayAlertErrors,
  FileType,
  FileUploader,
  TFileUploaderProps,
  TInitialFile,
  TUploadFile,
} from '@verifime/components';
import { DocumentType } from '@verifime/utils';
import { FC, useEffect, useState } from 'react';
import { z } from 'zod';

const uploadFileByPresignUrl = (
  presignUploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Add progress event listener
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        onProgress?.(percentComplete);
      }
    });

    // Add load event listener
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        console.error(`Upload failed with status ${xhr.status}`);
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    // Add error event listener
    xhr.addEventListener('error', (error) => {
      console.error('Upload error:', error);
      reject(new Error('Upload failed'));
    });

    // Open and send the request
    xhr.open('PUT', presignUploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};

export const entityDocumentApi = {
  upload: async (
    files: File[],
    onProgress: (progress: number, fileId: string) => void,
    entityId: TEntityDocumentUploader['entityId'],
    documentType: TEntityDocumentUploader['documentType'],
  ): Promise<{
    uploadedFiles: { url: string; id?: string }[];
    additionalDetails: Record<string, any>;
  }> => {
    try {
      const fileNames = files.map((file) => file.name);
      const presingUrlResponse = await api.postV2personIdidentityDocumentTypepresign(fileNames, {
        params: {
          id: entityId,
          type: documentType as
            | 'Passport'
            | 'DriverLicence'
            | 'MedicareCard'
            | 'OtherIdentityDocument',
        },
      });

      const scanFilesId = presingUrlResponse.scanFilesId as string;
      const presignUrls = presingUrlResponse.presignUrlList as z.infer<
        typeof apiSchemas.PresignUrl
      >[];
      if (!presignUrls?.length) {
        console.error('No presign upload urls found');
        throw new Error('Upload failed');
      }

      const uploadedFiles = await Promise.all(
        files.map(async (file, index) => {
          const presignUrl = presignUrls[index];
          if (!presignUrl?.url) {
            throw new Error('Invalid presign URL');
          }

          // Use a wrapper function to adapt the progress callback
          const progressWrapper = (progress: number) => {
            onProgress(progress, presignUrl.fileId);
          };

          await uploadFileByPresignUrl(presignUrl.url, file, progressWrapper);
          const fileUrl = URL.createObjectURL(file);
          return {
            url: fileUrl,
            id: presignUrl.fileId,
          };
        }),
      );

      return {
        uploadedFiles,
        additionalDetails: { scanFilesId },
      };
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error('Upload failed');
    }
  },

  delete: async (
    file: TUploadFile,
    entityId: TEntityDocumentUploader['entityId'],
    documentType: TEntityDocumentUploader['documentType'],
  ): Promise<any> => {
    try {
      return await api.deleteV2personIdidentityDocumentTypefileFileId(undefined, {
        params: {
          id: entityId,
          fileId: file.id,
          type: documentType as
            | 'DriverLicence'
            | 'MedicareCard'
            | 'OtherIdentityDocument'
            | 'Passport',
        },
      });
    } catch (error) {
      console.error('Delete error:', error);
    }
  },
  fetchInitialFiles: async ({
    entityId,
    documentType,
  }: {
    entityId: TEntityDocumentUploader['entityId'];
    documentType: TEntityDocumentUploader['documentType'];
  }): Promise<{ uploadedFiles: TInitialFile[]; additionalDetails: Record<string, any> }> => {
    try {
      // Get list of files info first
      const { scanFilesId, itemList: listFiles } = await api.getV2personIdidentityDocumentTypefile({
        params: {
          id: entityId,
          type: documentType as
            | 'DriverLicence'
            | 'MedicareCard'
            | 'OtherIdentityDocument'
            | 'Passport',
        },
      });

      if (listFiles?.length < 1) {
        return;
      }

      // Get all presign download
      const presignUrls = await Promise.all(
        listFiles.map((item) =>
          api.getV1fileEntityIdDocTypeFileIdpresignDownload({
            params: { entityId, docType: documentType, fileId: item.fileId },
          }),
        ),
      );

      return {
        uploadedFiles: presignUrls.map(({ url, fileId, filename }) => ({
          url,
          id: fileId,
          name: filename,
        })),
        additionalDetails: { scanFilesId },
      };
    } catch (error) {
      console.error('Error fetching initial file:', error);
      throw new Error('Fetching initial file failed');
    }
  },
};

type TEntityDocumentUploader = {
  entityId: string;
  documentType: DocumentType;
  onInitialFilesFetched?: (data: {
    uploadedFiles: TInitialFile[];
    additionalDetails: Record<string, any>;
  }) => void;
} & Omit<TFileUploaderProps, 'onUpload' | 'onDelete'>;

/**
 * Due to backend design, entity document upload needs two steps:
 * 
 * 1. Call the endpoint `POST /v1/file/presign` to get the presign upload url (let's say fileUploadUrl)
 * 2. Call `
 *  fetch(fileUploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': fileType },
      body: file,
    })
 * ` to upload the file
 * 
*/
export const EntityDocumentUploader: FC<TEntityDocumentUploader> = ({
  entityId,
  documentType,
  onInitialFilesFetched,
  ...props
}) => {
  const [initialFiles, setInitialFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!documentType) {
      return;
    }
    entityDocumentApi
      .fetchInitialFiles({ entityId, documentType })
      .then((data) => {
        if (!data) {
          setInitialFiles([]);
          return;
        }
        setInitialFiles(data.uploadedFiles || []);
        onInitialFilesFetched?.(data);
      })
      .catch((error) => setErrorMessage(error?.message || 'Unknown errors'));
  }, [entityId, documentType]);

  // Clear error message after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  return (
    <Box>
      {errorMessage && <DisplayAlertErrors errors={[{ message: errorMessage }]} />}
      <FileUploader
        hideUploaderAfterUpload
        hideUploaderOnInitialFiles
        acceptedFileTypes={[FileType.JPEG, FileType.PNG, FileType.PDF]}
        onUpload={(files, onProgress) =>
          entityDocumentApi.upload(files as File[], onProgress, entityId, documentType)
        }
        initialFiles={initialFiles}
        onDelete={(uploadedFile) => entityDocumentApi.delete(uploadedFile, entityId, documentType)}
        {...props}
      />
    </Box>
  );
};
